/**
 * SidebarLoader.ts
 * Handles loading and highlighting of the sidebar
 */

import { querySelector, querySelectorAll } from './DOMHelpers';

/**
 * Service for loading and managing the sidebar
 */
export class SidebarLoader {
  private readonly sidebarPath: string;
  private container: HTMLElement | null = null;

  constructor(sidebarPath: string = '/sidebar.html') {
    this.sidebarPath = sidebarPath;
  }

  /**
   * Load the sidebar HTML asynchronously
   */
  public async loadSidebar(): Promise<void> {
    // Query for container at load time (after layout is rendered)
    this.container = querySelector<HTMLElement>('#sidebar-container');

    if (!this.container) {
      console.warn('Sidebar container not found in DOM');
      return;
    }

    try {
      const html = await this.fetchSidebarHTML();
      this.injectHTML(html);
      this.highlightActiveItem();
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  /**
   * Highlight the active sidebar item based on data-active-sidebar attribute
   */
  public highlightActiveItem(activeName?: string): void {
    if (!this.container) return;

    // Get target from parameter or data attribute
    const target = activeName || document.body.dataset.activeSidebar;

    if (!target) {
      return;
    }

    // Clear previous selection
    const selectedItems = querySelectorAll<HTMLElement>(
      '.sidebar-item.selected-item',
      this.container
    );
    selectedItems.forEach(item => item.classList.remove('selected-item'));

    // Find and highlight ALL matching items (supports dual-highlight for entries in multiple sections)
    const items = querySelectorAll<HTMLElement>('.sidebar-item', this.container);
    const matchingItems = Array.from(items).filter(
      item => item.textContent?.trim() === target
    );

    matchingItems.forEach(item => {
      item.classList.add('selected-item');
    });
  }

  /**
   * Fetch sidebar HTML with cache busting
   */
  private async fetchSidebarHTML(): Promise<string> {
    const cacheBuster = Date.now();
    const url = `${this.sidebarPath}?v=${cacheBuster}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to load sidebar: ${response.statusText}`);
    }

    return await response.text();
  }

  /**
   * Inject HTML into the container
   */
  private injectHTML(html: string): void {
    if (this.container) {
      this.container.innerHTML = html;
    }
  }

  /**
   * Handle errors during sidebar loading
   */
  private handleError(error: Error): void {
    console.error('Failed to load sidebar:', error);

    if (this.container) {
      this.container.innerHTML = `
        <div class="sidebar-error">
          <p>Failed to load sidebar</p>
          <button onclick="location.reload()">Retry</button>
        </div>
      `;
    }
  }

  /**
   * Reload the sidebar
   */
  public async reload(): Promise<void> {
    await this.loadSidebar();
  }
}
